import { adminFetch } from '../../lib/adminFetch';
import React, { useState, useEffect, useRef } from 'react';
import ImageCropper from '../../components/admin/ImageCropper';

const emptyPartner = {
  id: '',
  name: '',
  representative: '',
  prefecture: '',
  postalCode: '',
  address: '',
  phone: '',
  email: '',
  website: '',
  established: '',
  experience: '',
  supportedCompanies: '',
  isFounder: false,
  services: [],
  description: '',
  message: '',
  achievements: '',
  representativeImage: {},
  representativeImagePosition: 'center',
  logo: {},
  image: [],
  imagePosition: 'center',
};

const prefectures = [
  '北海道','青森県','岩手県','宮城県','秋田県','山形県','福島県',
  '茨城県','栃木県','群馬県','埼玉県','千葉県','東京都','神奈川県',
  '新潟県','富山県','石川県','福井県','山梨県','長野県','岐阜県','静岡県','愛知県',
  '三重県','滋賀県','京都府','大阪府','兵庫県','奈良県','和歌山県',
  '鳥取県','島根県','岡山県','広島県','山口県',
  '徳島県','香川県','愛媛県','高知県',
  '福岡県','佐賀県','長崎県','熊本県','大分県','宮崎県','鹿児島県','沖縄県',
];

const serviceOptions = [
  '労務顧問','給与計算','就業規則','助成金','社会保険手続','労働保険手続',
  '人事制度設計','評価制度','採用支援','研修','メンタルヘルス','労務監査',
];

const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('files[]', file);
  const res = await adminFetch('/api/admin-files.php?action=upload', { method: 'POST', body: formData });
  const data = await res.json();
  if (data.success) return '/uploads/' + file.name;
  return null;
};

const ImageUploader = ({ label, url, position, onUrlChange, onPositionChange }) => {
  const ref = useRef(null);
  const cropperRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      cropperRef.current?.openWithUrl(ev.target.result);
    };
    reader.readAsDataURL(file);
    if (ref.current) ref.current.value = '';
  };

  return (
    <div>
      <label className="block text-sm font-bold mb-1">{label}</label>
      {url ? (
        <div className="mb-2">
          {/* トリミング結果のプレビュー */}
          <div className="w-28 h-28 rounded border overflow-hidden mb-2">
            <img src={url} alt="" className="w-full h-full object-cover" style={{ objectPosition: position || 'center' }} />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {onPositionChange && (
              <button onClick={() => setCropOpen(true)} className="text-xs text-blue-600 hover:text-blue-800 font-bold">位置調整</button>
            )}
            <button onClick={() => ref.current?.click()} className="text-xs text-blue-600 hover:text-blue-800 font-bold">変更</button>
            <button onClick={() => { onUrlChange(''); if (onPositionChange) onPositionChange('center'); }} className="text-xs text-red-500 hover:text-red-700">削除</button>
          </div>
        </div>
      ) : (
        <div className="w-28 h-28 bg-gray-100 rounded border flex items-center justify-center mb-2">
          <span className="text-xs text-gray-400">なし</span>
        </div>
      )}
      <input type="file" ref={ref} onChange={handleUpload} accept="image/*" className="hidden" />
      <div className="flex gap-2">
        {!url && (
          <button onClick={() => ref.current?.click()} disabled={uploading} className="text-sm text-blue-600 hover:text-blue-800 font-bold disabled:opacity-50">
            {uploading ? 'アップロード中...' : 'アップロード'}
          </button>
        )}
        <ImageCropper
          ref={cropperRef}
          aspectRatio={1}
          maxWidth={400}
          maxHeight={400}
          showUploadButton={false}
          onCropped={(img) => onUrlChange(img.url)}
        />
      </div>

      {/* 位置調整モーダル */}
      {cropOpen && (
        <CropModal
          url={url}
          position={position}
          onSave={(pos) => { onPositionChange(pos); setCropOpen(false); }}
          onClose={() => setCropOpen(false)}
        />
      )}
    </div>
  );
};

const CropModal = ({ url, position, onSave, onClose }) => {
  const imgRef = useRef(null);
  const boxW = 40;
  const boxH = 50;

  const initCenter = (() => {
    if (!position || position === 'center') return { x: 50, y: 50 };
    const parts = position.split(' ').map((s) => parseInt(s));
    return { x: parts[0] || 50, y: parts[1] || 50 };
  })();

  const [center, setCenter] = useState(initCenter);
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const getPercent = (e) => {
    if (!imgRef.current) return { x: 50, y: 50 };
    const rect = imgRef.current.getBoundingClientRect();
    return {
      x: Math.round(((e.clientX - rect.left) / rect.width) * 100),
      y: Math.round(((e.clientY - rect.top) / rect.height) * 100),
    };
  };

  const clamp = (cx, cy) => ({
    x: Math.max(boxW / 2, Math.min(100 - boxW / 2, cx)),
    y: Math.max(boxH / 2, Math.min(100 - boxH / 2, cy)),
  });

  const handleMouseDown = (e) => {
    e.preventDefault();
    const p = getPercent(e);
    setDragOffset({ x: p.x - center.x, y: p.y - center.y });
    setDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    const p = getPercent(e);
    setCenter(clamp(p.x - dragOffset.x, p.y - dragOffset.y));
  };

  const handleMouseUp = () => setDragging(false);

  const handleWheel = (e) => {
    e.preventDefault();
    setZoom((prev) => Math.max(0.5, Math.min(3, prev + (e.deltaY > 0 ? -0.1 : 0.1))));
  };

  // 枠の範囲
  const x1 = center.x - boxW / 2;
  const y1 = center.y - boxH / 2;

  // プレビュー: 枠の中身をそのまま切り出して表示
  const previewStyle = {
    position: 'absolute',
    left: `-${(x1 / boxW) * 100}%`,
    top: `-${(y1 / boxH) * 100}%`,
    width: `${(100 / boxW) * 100}%`,
    height: 'auto',
  };

  const rectStyle = {
    left: x1 + '%',
    top: y1 + '%',
    width: boxW + '%',
    height: boxH + '%',
  };

  const pos = `${center.x}% ${center.y}%`;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl max-w-3xl w-full p-6">
        <h3 className="font-bold text-lg mb-2">表示位置を調整</h3>
        <p className="text-sm text-gray-500 mb-3">白い枠をドラッグして移動。スクロールで拡大縮小。枠の中身がそのまま表示されます。</p>

        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => setZoom((z) => Math.max(0.5, z - 0.2))} className="w-8 h-8 bg-gray-100 rounded-lg text-lg font-bold hover:bg-gray-200 flex items-center justify-center">-</button>
          <input type="range" min="0.5" max="3" step="0.1" value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} className="flex-1 h-2 accent-blue-600" />
          <button onClick={() => setZoom((z) => Math.min(3, z + 0.2))} className="w-8 h-8 bg-gray-100 rounded-lg text-lg font-bold hover:bg-gray-200 flex items-center justify-center">+</button>
          <span className="text-xs text-gray-400 w-12 text-right">{Math.round(zoom * 100)}%</span>
        </div>

        <div className="grid grid-cols-[1fr_180px] gap-4">
          {/* 左: 元画像 + 枠 */}
          <div className="rounded border overflow-auto bg-gray-200" style={{ maxHeight: '60vh' }} onWheel={handleWheel}>
            <div
              ref={imgRef}
              className="relative select-none"
              style={{ cursor: dragging ? 'grabbing' : 'grab', width: `${zoom * 100}%` }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <img src={url} alt="" className="w-full h-auto block" draggable={false} style={{ opacity: 0.4 }} />
              {/* 枠の中だけ明るく */}
              <div className="absolute overflow-hidden pointer-events-none" style={rectStyle}>
                <img
                  src={url}
                  alt=""
                  className="absolute block"
                  draggable={false}
                  style={{
                    width: imgRef.current ? imgRef.current.offsetWidth + 'px' : '100%',
                    height: 'auto',
                    left: -x1 + '%',
                    top: -y1 + '%',
                    maxWidth: 'none',
                  }}
                />
              </div>
              <div className="absolute border-2 border-white pointer-events-none" style={rectStyle} />
            </div>
          </div>

          {/* 右: プレビュー（枠の中身をそのまま表示） */}
          <div>
            <p className="text-xs text-gray-400 mb-1">プレビュー</p>
            <div className="w-full aspect-[4/5] rounded border overflow-hidden relative mb-3">
              <img src={url} alt="" className="absolute block" draggable={false} style={previewStyle} />
            </div>
            <div className="w-full aspect-[3/2] rounded border overflow-hidden relative">
              <img
                src={url}
                alt=""
                className="absolute block"
                draggable={false}
                style={{
                  position: 'absolute',
                  left: `-${(x1 / boxW) * 100}%`,
                  top: `-${(y1 / boxH) * 100}%`,
                  width: `${(100 / boxW) * 100}%`,
                  height: 'auto',
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 justify-end mt-4">
          <button onClick={() => { setCenter({ x: 50, y: 50 }); setZoom(1); }} className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2">リセット</button>
          <button onClick={onClose} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">キャンセル</button>
          <button onClick={() => onSave(pos)} className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">決定</button>
        </div>
      </div>
    </div>
  );
};

const PartnerAdmin = () => {
  const [partners, setPartners] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPartner);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    adminFetch('/api/admin-read.php?file=partners&t=' + Date.now())
      .then((r) => r.ok ? r.json() : [])
      .then(setPartners)
      .catch(() => setPartners([]));
  }, []);

  const startNew = () => {
    setForm({ ...emptyPartner, id: Date.now() });
    setEditing('new');
    setMessage('');
  };

  const startEdit = (index) => {
    const p = partners[index];
    setForm({ ...emptyPartner, ...p, services: [...(p.services || [])], image: [...(p.image || [])] });
    setEditing(index);
    setMessage('');
  };

  const cancel = () => { setEditing(null); setForm(emptyPartner); };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleService = (service) => {
    setForm((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }));
  };

  const save = async () => {
    setSaving(true);
    setMessage('');
    let updated;
    if (editing === 'new') {
      updated = [...partners, form];
    } else {
      updated = partners.map((p, i) => (i === editing ? form : p));
    }
    try {
      const res = await adminFetch('/api/admin-data.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: 'partners', data: updated }),
      });
      const data = await res.json();
      if (data.success) {
        setPartners(updated);
        setEditing(null);
        setForm(emptyPartner);
        setMessage('保存しました');
      } else {
        setMessage('保存に失敗しました');
      }
    } catch {
      setMessage('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (index) => {
    if (!window.confirm('削除しますか？')) return;
    const updated = partners.filter((_, i) => i !== index);
    try {
      await adminFetch('/api/admin-data.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: 'partners', data: updated }),
      });
      setPartners(updated);
      setMessage('削除しました');
    } catch {
      setMessage('削除に失敗しました');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">パートナー事務所管理</h2>
        {editing === null && (
          <button onClick={startNew} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">
            + 新規追加
          </button>
        )}
      </div>

      {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

      {/* プレビュー */}
      {editing !== null && form.name && (
        <div className="mb-6 bg-white rounded-xl shadow p-4">
          <p className="text-xs font-bold text-gray-400 mb-3">プレビュー（パートナーページでの表示イメージ）</p>
          <div className="max-w-xs mx-auto bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
            {/* 写真 */}
            <div className="h-36 bg-gray-100 flex items-center justify-center overflow-hidden">
              {form.image?.[0]?.url ? (
                <img src={form.image[0].url} alt="" className="w-full h-full object-cover" style={{ objectPosition: form.imagePosition || 'center' }} />
              ) : form.representativeImage?.url ? (
                <img src={form.representativeImage.url} alt="" className="w-full h-full object-cover" style={{ objectPosition: form.representativeImagePosition || 'center' }} />
              ) : (
                <span className="text-4xl font-bold text-gray-300">{(form.name || '?').charAt(0)}</span>
              )}
            </div>
            <div className="p-4">
              {/* ロゴ */}
              {form.logo?.url && (
                <div className="h-8 mb-2">
                  <img src={form.logo.url} alt="" className="h-full object-contain" />
                </div>
              )}
              <h4 className="font-bold text-sm mb-1" style={{color: '#403737'}}>{form.name}</h4>
              <p className="text-xs text-gray-500 mb-2">代表: {form.representative}</p>
              {form.prefecture && (
                <span className="inline-block text-xs text-white px-2 py-0.5 rounded-full" style={{backgroundColor: '#D8C5C9'}}>
                  {form.address ? form.address.split('市')[0] + '市' : form.prefecture}
                </span>
              )}
              {form.services && form.services.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {form.services.slice(0, 6).map((s, i) => (
                    <span key={i} className="text-[9px] text-white px-1.5 py-0.5 rounded font-bold" style={{backgroundColor: '#B88A8F'}}>
                      {s}
                    </span>
                  ))}
                  {form.services.length > 6 && <span className="text-[9px] text-gray-400">+{form.services.length - 6}</span>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {editing !== null && (
        <div className="bg-white rounded-xl shadow p-6 mb-6">
          <h3 className="font-bold text-lg mb-4">{editing === 'new' ? '新規追加' : '編集'}</h3>
          {/* 基本情報 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="事務所名" value={form.name} onChange={(v) => updateField('name', v)} />
            <Field label="代表者名" value={form.representative} onChange={(v) => updateField('representative', v)} />
            <div>
              <label className="block text-sm font-bold mb-1">都道府県</label>
              <select
                value={form.prefecture}
                onChange={(e) => updateField('prefecture', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">選択してください</option>
                {prefectures.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <Field label="郵便番号" value={form.postalCode || ''} onChange={(v) => updateField('postalCode', v)} />
            <Field label="住所" value={form.address} onChange={(v) => updateField('address', v)} />
            <Field label="電話番号" value={form.phone || ''} onChange={(v) => updateField('phone', v)} />
            <Field label="メールアドレス" value={form.email || ''} onChange={(v) => updateField('email', v)} />
            <Field label="Webサイト" value={form.website || ''} onChange={(v) => updateField('website', v)} />
          </div>

          {/* 実績情報 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <Field label="設立年" value={form.established || ''} onChange={(v) => updateField('established', v)} />
            <Field label="経験年数" value={form.experience || ''} onChange={(v) => updateField('experience', v)} />
            <Field label="支援企業数" value={form.supportedCompanies || ''} onChange={(v) => updateField('supportedCompanies', v)} />
          </div>
          <div className="mt-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isFounder || false}
                onChange={(e) => updateField('isFounder', e.target.checked)}
                className="w-4 h-4"
              />
              <span className="font-bold">SrWith創設メンバー</span>
            </label>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-bold mb-2">対応サービス</label>
            <div className="flex flex-wrap gap-2">
              {serviceOptions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleService(s)}
                  className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                    form.services.includes(s)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* 画像アップロード */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <ImageUploader
              label="代表者写真"
              url={form.representativeImage?.url || ''}
              position={form.representativeImagePosition || 'center'}
              onUrlChange={(url) => updateField('representativeImage', url ? { ...form.representativeImage, url } : {})}
              onPositionChange={(pos) => updateField('representativeImagePosition', pos)}
            />
            <ImageUploader
              label="ロゴ"
              url={form.logo?.url || ''}
              onUrlChange={(url) => updateField('logo', url ? { url } : {})}
            />
            <ImageUploader
              label="事務所写真"
              url={form.image?.[0]?.url || ''}
              position={form.imagePosition || 'center'}
              onUrlChange={(url) => updateField('image', url ? [{ url }] : [])}
              onPositionChange={(pos) => updateField('imagePosition', pos)}
            />
          </div>

          <div className="mt-4">
            <Field label="紹介文" value={form.description || ''} onChange={(v) => updateField('description', v)} textarea />
          </div>
          <div className="mt-4">
            <Field label="代表者メッセージ" value={form.message || ''} onChange={(v) => updateField('message', v)} textarea />
          </div>
          <div className="mt-4">
            <Field label="実績・強み" value={form.achievements || ''} onChange={(v) => updateField('achievements', v)} textarea />
          </div>

          <div className="flex gap-3 mt-6">
            <button onClick={save} disabled={saving} className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
              {saving ? '保存中...' : '保存'}
            </button>
            <button onClick={cancel} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">
              キャンセル
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {partners.length === 0 && editing === null && (
          <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
            パートナー事務所がありません。
          </div>
        )}
        {partners.map((p, i) => (
          <div key={p.id || i} className="bg-white rounded-xl shadow p-5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* サムネイル */}
              <div className="w-12 h-12 bg-gray-100 rounded border flex items-center justify-center shrink-0 overflow-hidden">
                {p.representativeImage?.url ? (
                  <img src={p.representativeImage.url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-lg font-bold text-gray-400">{(p.name || '?').charAt(0)}</span>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-gray-100 text-gray-600 text-xs font-bold px-2 py-0.5 rounded">{p.prefecture}</span>
                </div>
                <p className="font-bold">{p.name}</p>
                <p className="text-sm text-gray-500">代表: {p.representative}</p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => startEdit(i)} className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-bold hover:bg-gray-200">編集</button>
              <button onClick={() => remove(i)} className="bg-red-50 text-red-600 px-3 py-1.5 rounded-lg text-sm font-bold hover:bg-red-100">削除</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, textarea }) => (
  <div>
    <label className="block text-sm font-bold mb-1">{label}</label>
    {textarea ? (
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
    ) : (
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
    )}
  </div>
);

export default PartnerAdmin;
